/**
 * The host's refund policy for a paid drop-in, tournament or league. Refunds
 * are the host's call: Sportifide shows the policy to players before they pay
 * and gives the host a Refund button on each payment (/payments).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { dollarsToCents, usePaymentsSetup } from './PriceField';

export const REFUND_POLICY_MAX = 1000;

const noteStyle = { fontSize: '0.78rem', margin: '0.3rem 0 0', color: 'rgba(255,255,255,0.6)' };

/** Optional policy box under a price: shown while payments are on and there is a price. */
export function RefundPolicyField({ value, onChange, price, setup: given }) {
  const own = usePaymentsSetup();
  const { enabled } = given || own;
  if (!enabled || !(dollarsToCents(price) > 0)) return null;

  return (
    <div className="form-group-gaming" data-testid="refund-policy-field">
      <label className="form-label-gaming" htmlFor="refund-policy-input">Refund policy (optional)</label>
      <textarea
        id="refund-policy-input"
        className="form-input-gaming"
        rows={3}
        maxLength={REFUND_POLICY_MAX}
        placeholder="e.g. Full refund up to 48 hours before. No refunds after the first game."
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
      <p style={noteStyle}>
        Players see this before they pay. Refunds are your call: you can refund any payment from Payments.
      </p>
    </div>
  );
}

/** The policy as players read it, next to the price or Pay button. */
export function RefundPolicyNote({ policy, style }) {
  const text = typeof policy === 'string' ? policy.trim() : '';
  if (!text) return null;
  return (
    <div
      data-testid="refund-policy"
      style={{
        fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '0.5rem 0.7rem',
        margin: '0.5rem 0', whiteSpace: 'pre-wrap', ...style,
      }}
    >
      <strong>Refund policy:</strong> {text}
    </div>
  );
}

/**
 * What a player reads before leaving (or withdrawing from) something they may
 * have paid for. Leaving never refunds by itself: refunds are the host's call.
 *   what     'this drop-in' | 'this tournament' | 'this league'
 *   paid     the player's payment went through and wasn't refunded
 *   started  the event has started (or its time has passed)
 *   policy   the host's refund policy text, if any
 *   rejoin   true when coming back keeps the payment (drop-ins)
 */
export function leaveMessage({ what = 'this', paid = false, started = false, policy, rejoin = false } = {}) {
  const ask = `Are you sure you want to leave ${what}?`;
  if (!paid) return ask;
  if (started) {
    return `${ask} It has already started, so no refund is made. Contact the host if you think you're owed one.`;
  }
  const text = typeof policy === 'string' ? policy.trim() : '';
  return [
    ask,
    "Your payment isn't refunded automatically: refunds are up to the host.",
    text ? `Host's refund policy: ${text}${/[.!?]$/.test(text) ? '' : '.'}` : 'The host has not written a refund policy, so ask them.',
    rejoin ? 'If you join again, your payment still counts.' : '',
  ].filter(Boolean).join(' ');
}

/** For the host of a paid event: where every payment received is listed, each with Refund. */
export function HostPaymentsLink({ style }) {
  return (
    <Link
      to="/payments"
      data-testid="host-payments-link"
      style={{ display: 'inline-block', fontSize: '0.82rem', color: '#7dd3fc', margin: '0.4rem 0', ...style }}
    >
      💳 Payments received: see who paid and refund from here
    </Link>
  );
}
